import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Handling timezones in python",
  "description": "",
  "published": "2011-11-02T00:00:00.000Z",
  "redirect_from": ["/blog/handling-timezones-in-python/swizec/2835"],
  "categories": "Coordinated Universal Time, Daylight saving time, Lists of time zones, Telstar Logistics, timezone, Uncategorized, UTC",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/433029904_27dd44a95a_m3.jpg",
        "alt": "Time Selector",
        "title": "Time Selector"
      }}></img></p>
    <p>{`A couple of days ago I started refactoring some code to handle `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Lists_of_time_zones",
        "title": "Lists of time zones"
      }}>{`timezones`}</a>{` better. The problem manifests as a single time appearing at different times in different parts of the application.`}</p>
    <p>{`Granted, a lot of it is more of a UX problem along the lines of my rant on why `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/why-dont-computers-understand-timezones/swizec/2288",
        "title": "Why don’t computers understand timezones?"
      }}>{`computers don't handle timezones intuitively`}</a>{`. But there's a deeper issue with the fact that the codebase is handling timezones rather naively.`}</p>
    <p>{`Until a week ago I would have done it exactly the same way.`}</p>
    <h2 {...{
      "id": "naive-approach"
    }}>{`Naive approach`}</h2>
    <p>{`Usually when people first implement timing in an application they (or rather we) don't really care about timezones. You just take a timestamp and store it in a database. This suffices for most use cases ... sure the times are in whatever timezone your server is set to but as long as you're using the same server for everything and you're only using times for timestamps when objects were inserted into the database and for logging ...`}</p>
    <p>{`It's just not a problem.`}</p>
    <p>{`Then you are faced with a distributed environment and suddenly all those timestamps start acting funny, or maybe you have users in different timezones and you want to display times to them.`}</p>
    <p>{`Still an easy problem to solve; just handle all times as UTC internally and translate to the proper timezone when producing outputs. Easy as pie right?`}</p>
    <p>{`And then you notice strange things happen around daylight savings. Suddenly 3am happens twice on the 31st of October in Slovenia ... but the same thing only happens a week later in the US. And some states in the US don't even observe `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Daylight_saving_time",
        "title": "Daylight saving time"
      }}>{`DST`}</a>{` and now what do you do?`}</p>
    <h2 {...{
      "id": "pytz"
    }}>{`pyTZ`}</h2>
    <p>{`Those ambiguous periods are the main thing pytz takes care of.`}</p>
    <p>{`To be perfectly honest I never even considered this problem before reading the documentation and having my mind blown. There's just so much intricacies over keeping up to date on what each timezone is doing and what particular timezone a user is in right now ... it's insane!`}</p>
    <p>{`Even though `}<a parentName="p" {...{
        "href": "http://pypi.python.org/pypi/pytz"
      }}>{`pytz`}</a>{` claims they don't take care of ambiguous periods like Poland rewinding their clocks half an hour in 1915 to start using `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Central_European_Time",
        "title": "Central European Time"
      }}>{`CET`}</a>{`, they're still pretty vital for everything else.`}</p>
    <p>{`Of course you'll still want to handle all times internally as UTC otherwise doing time arithmetic can get quite hairy, although pytz can supposedly handle that too ... but at least now it should be possible to correctly get the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Coordinated_Universal_Time",
        "title": "Coordinated Universal Time"
      }}>{`UTC time`}</a>{` out of user input and then correctly display the result.`}</p>
    <p>{`An example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from datetime import datetime
from pytz import *

eastern = timezone('US/Eastern')
loc_dt = eastern.localize(datetime(2011, 11, 2, 7, 27, 0))
print loc_dt
# 2011-11-02 07:27:00-04:00

ljubljana = timezone("Europe/Ljubljana")
print loc_dt.astimezone(ljubljana)
# 2011-11-02 12:27:00+01:00
`}</code></pre>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`Turns out there's much more to timezones than one might think. Just goes to show why all those `}<em parentName="p">{`other`}</em>{` coders are spending so much time on trivial stuff ... nothing is really trivial. Nothing.`}</p>
    <p>{`And let's not even get into `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Leap_second",
        "title": "Leap second"
      }}>{`leap seconds`}</a>{`.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://lucumr.pocoo.org/2011/7/15/eppur-si-muove/"
        }}>{`Dealing with Timezones in Python`}</a>{` (lucumr.pocoo.org)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://thebloggingpath.com/2011/08/28/timestamps-and-timezones/"
        }}>{`Timestamps and timezones...`}</a>{` (thebloggingpath.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.sebastianmarshall.com/just-say-no-to-online-calendars"
        }}>{`Just Say No to Online Calendars...`}</a>{` (sebastianmarshall.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://unix4lyfe.org/time/"
        }}>{`What every programmer should know about time`}</a>{` (unix4lyfe.org)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=ae6a7b39-c233-48c2-bb4b-4d47940ca4b8",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      